/**
 * Builds the serializable model for the Product / Project circularity HTML report.
 * The model holds plain text and numbers only: no HTML, internal IDs, tokens or API URLs.
 * Design Assessment factors (CT / CA / ID / GPE) are intentionally left out; only the
 * resulting PCI values are carried.
 */
import { toFiniteNumber } from "../../functions/results/formatters";
import { INPUT_CIRCULAR_KEYS, OUTPUT_CIRCULAR_KEYS, summarizeCircularityFlows, virginInputPercent, wasteOutputPercent } from "../../functions/results/circularityResults";
import { REPORT_SCHEMA_VERSION, REPORT_TYPES, plainText } from "./reportModel";

// Scope & Boundary "flow" values and their display labels.
const SYSTEM_BOUNDARY_LABELS = { gate: "Cradle to gate", grave: "Cradle to grave" };

const METHODOLOGY_TEXT = [
  [
    "Material Circularity Indicator (MCI)",
    "Calculated per material with the Ellen MacArthur Foundation method: MCI = 1 - LFI x F(X), where the Linear Flow Index (LFI) combines virgin input and unrecoverable waste, and F(X) = 0.9 / X adjusts for the utility (service life and functional units relative to the industry average). The overall value is the mass-weighted average of each material's MCI, rescaled so that a fully linear material scores 0%.",
  ],
  [
    "Circularity Indicator (PCI)",
    "Combines each material's MCI with its disassembly potential (the harmonic mean of the connection type, connection accessibility, independency and edge geometry factors). The overall value is the mass-weighted average across materials.",
  ],
  [
    "Material flows",
    "Input shares are recycled, reused, remanufactured and biological content; the remainder is virgin feedstock. End-of-life shares are recycling, reuse, composting and energy recovery; the remainder is waste sent to landfill.",
  ],
  [
    "Embodied carbon",
    "Global warming potential (GWP) of each material as calculated in the life cycle assessment, in kg CO₂e.",
  ],
];

const pickPercentages = (row, keys) => Object.fromEntries(keys.map((key) => [key, toFiniteNumber(row[key])]));

const buildScope = ({ entity, topic, boundaryState, serviceLife, translate }) => {
  const boundary = boundaryState || {};
  return [
    { label: translate(`${topic} name`), value: plainText(entity?.name) },
    { label: translate("Sub category"), value: plainText(entity?.sub_category) },
    { label: translate("Period start"), value: plainText(boundary.start) },
    { label: translate("Period end"), value: plainText(boundary.end) },
    { label: translate("Cut-off"), value: plainText(boundary.cutoff) },
    { label: translate("System boundary"), value: boundary.flow ? translate(SYSTEM_BOUNDARY_LABELS[boundary.flow] || plainText(boundary.flow)) : "" },
    { label: translate("Materiality threshold"), value: boundary.materiality !== undefined && boundary.materiality !== null ? `${toFiniteNumber(boundary.materiality)}%` : "" },
    { label: translate("Service life"), value: toFiniteNumber(serviceLife) > 0 ? `${toFiniteNumber(serviceLife)} ${translate("years")}` : "" },
  ].filter((item) => item.value !== "");
};

/**
 * @param {object} entity - selected product / project ({ name, sub_category })
 * @param {"Product"|"Project"} topic
 * @param {object} combined_data - output of calculate_combined_data
 * @param {object} pciData - output of computePciData for the same rows
 * @param {object} boundaryState - Scope & Boundary values ({ start, end, cutoff, flow, materiality })
 * @param {number} serviceLife - product / project service life in years
 * @param {function} translate - i18n t function
 * @param {string} language
 */
export const buildCircularityReport = ({ entity, topic = "Product", combined_data, pciData, boundaryState, serviceLife, translate, language }) => {
  const rows = combined_data?.combined_circularities || [];
  const pciRows = pciData?.rows || [];

  const materials = rows.map((row, index) => ({
    rowKey: String(index + 1),
    description: plainText(row.description) || translate("Unknown Material"),
    type: plainText(row.type),
    category: plainText(row.category),
    subCategory: plainText(row.sub_category),
    massKg: toFiniteNumber(row.mass),
    gwpKg: toFiniteNumber(row.gwp_total),
    ...pickPercentages(row, INPUT_CIRCULAR_KEYS),
    ...pickPercentages(row, OUTPUT_CIRCULAR_KEYS),
    im_virgin_content: virginInputPercent(row),
    om_waste_content: wasteOutputPercent(row),
    eolRecyclingEfficiency: toFiniteNumber(row.eol_recycling_efficiency),
    eolFeedstockEfficiency: toFiniteNumber(row.eol_feedstock_efficiency),
    productLife: toFiniteNumber(row.uf_product_life),
    industryLife: toFiniteNumber(row.uf_industry_avg_life),
    utilityFactor: toFiniteNumber(row.uf_calculations),
    lfi: toFiniteNumber(row.LFI),
    mciPercent: toFiniteNumber(row.percentage_mci) * 100,
    mciContribution: toFiniteNumber(row.mci_contribution),
    pciPercent: toFiniteNumber(pciRows[index]?.percentage_pci),
  }));

  return {
    schemaVersion: REPORT_SCHEMA_VERSION,
    reportType: REPORT_TYPES.circularity,
    meta: {
      entityName: plainText(entity?.name) || translate(topic),
      entityKind: translate(topic),
      topic,
      reportingYear: null,
      generatedAt: new Date().toISOString(),
      language: language || "en",
    },
    summary: {
      massKg: toFiniteNumber(combined_data?.mass_sum),
      gwpKg: toFiniteNumber(combined_data?.gwp_total_sum),
      mciPercent: toFiniteNumber(combined_data?.percentage_product_mci) * 100,
      pciPercent: toFiniteNumber(pciData?.percentageOverallPci),
      materialCount: materials.length,
      ...summarizeCircularityFlows(rows),
    },
    scope: buildScope({ entity, topic, boundaryState, serviceLife, translate }),
    materials,
    methodology: METHODOLOGY_TEXT.map(([label, value]) => ({
      label: translate(label === "Circularity Indicator (PCI)" ? `${topic} Circularity Indicator (PCI)` : label),
      value: translate(value),
    })),
  };
};
